// FILE: useCreateThreadFromDialog.ts
// Purpose: Turn a confirmed "New thread" dialog into a real thread.
// Layer: Web hooks
//
// Unlike `useHandleNewThread`, which only seeds a local draft and lets the
// first message materialize everything, this creates the worktree and the
// server thread up front — so a setup action can already be installing
// dependencies while the user is still typing the first prompt.
//
// The ordering here (seed draft → navigate → dispatch) is not incidental: it
// mirrors `useHandleNewWorktreeThread`, where the comments explain why the
// route guard needs the draft to exist first and why `thread.create` must come
// after the navigation.

import {
  DEFAULT_PROVIDER_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  type ProjectId,
  type ThreadId,
} from "@taskin-code/contracts";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";

import { useComposerDraftStore } from "../composerDraftStore";
import { createFreshDraftThreadSeed } from "../lib/threadBootstrap";
import { newCommandId, newThreadId } from "../lib/utils";
import { readNativeApi } from "../nativeApi";
import { usePendingSetupScriptStore } from "../pendingSetupScriptStore";
import { selectProjectPlanModeByDefault, useProjectConfigStore } from "../projectConfigStore";
import { setupProjectScript } from "@taskin-code/shared/projectScripts";
import { useStore } from "../store";
import { useTemporaryThreadStore } from "../temporaryThreadStore";
import { useTerminalStateStore } from "../terminalStateStore";
import { toastManager } from "../components/ui/toast";
import { describeWorktreeBaseFallback } from "../worktreeBase";
import type { NewThreadDialogSubmit } from "../components/NewThreadDialog";
import { openThreadAndDispatchCreate } from "./openThreadAndDispatchCreate";

/** Fallback title until the first turn generates a real one. */
const PLACEHOLDER_THREAD_TITLE = "New thread";

export function useCreateThreadFromDialog() {
  const navigate = useNavigate();
  const openChatThreadPage = useTerminalStateStore((store) => store.openChatThreadPage);
  const markTemporaryThread = useTemporaryThreadStore((store) => store.markTemporaryThread);
  const markPendingSetupScript = usePendingSetupScriptStore(
    (store) => store.markPendingSetupScript,
  );

  return useCallback(
    async (input: NewThreadDialogSubmit): Promise<void> => {
      const api = readNativeApi();
      if (!api) {
        throw new Error("Not connected to the server.");
      }

      const threadId = newThreadId();
      const createdAt = new Date().toISOString();
      const trimmedName = input.name.trim();
      // An environment already *is* a workspace: preparing a worktree here
      // would create a branch and a directory the server then ignores, because
      // the decider takes cwd, branch and mode from the environment. That is
      // exactly what the silent auto-assignment used to do — an orphan worktree
      // per thread, left behind for nobody.
      const usesEnvironment = input.environmentId !== null;
      const wantsWorktree = input.envMode === "worktree" && !usesEnvironment;

      let branch: string | null = null;
      let worktreePath: string | null = null;

      if (wantsWorktree && input.pullRequest) {
        // A pull request is not a fresh branch off the base, so `prepareThreadWorktree` is the
        // wrong call: it would cut a new branch and the thread would open on nothing. This one
        // fetches the pull request's head and checks it out into the worktree.
        //
        // It addresses the repository by cwd rather than by project id — that is the shape of
        // the git surface — so the project has to be resolved here.
        const project = useStore.getState().projects.find((entry) => entry.id === input.projectId);
        if (!project?.cwd) {
          throw new Error("No se pudo resolver el directorio del proyecto.");
        }
        const prepared = await api.git.preparePullRequestThread({
          cwd: project.cwd,
          reference: String(input.pullRequest.number),
          mode: "worktree",
        });
        branch = prepared.branch;
        worktreePath = prepared.worktreePath;
      } else if (wantsWorktree) {
        const prepared = await api.workspace.prepareThreadWorktree({
          projectId: input.projectId,
          threadId,
          ...(trimmedName.length > 0 ? { branchName: trimmedName } : {}),
        });
        branch = prepared.branch;
        worktreePath = prepared.worktreePath;

        // Same warning as the other worktree paths: a thread that forked from a
        // stale base is indistinguishable from a fresh one until it conflicts.
        if (prepared.base?.source === "local" && prepared.base.reason !== "configured-local") {
          toastManager.add({
            type: "warning",
            title: `Se partió de la copia local de "${prepared.baseBranch}"`,
            description: `${describeWorktreeBaseFallback(prepared.base.reason)} La rama puede estar por detrás del remoto.`,
          });
        }
      }

      // Nothing has been said yet, so the thread is disposable until the first
      // message promotes it (see `ChatView`). Abandoning the dialog's result
      // then costs nothing: switching threads cleans it up, worktree included.
      markTemporaryThread(threadId);

      const planModeByDefault = selectProjectPlanModeByDefault(
        useProjectConfigStore.getState(),
        input.projectId,
      );

      // Seed the draft before navigating so the route guard in
      // `_chat.$threadId.tsx` finds the thread and doesn't redirect to `/`.
      useComposerDraftStore.getState().setProjectDraftThreadId(input.projectId, threadId, {
        ...createFreshDraftThreadSeed({
          createdAt,
          entryPoint: "chat",
          options: {
            branch,
            worktreePath,
            envMode: input.envMode,
            temporary: true,
            planeIssue: input.planeIssue,
            pullRequest: input.pullRequest
              ? { projectId: input.pullRequest.projectId, number: input.pullRequest.number }
              : null,
          },
        }),
        ...(planModeByDefault ? { interactionMode: "plan" as const } : {}),
      });
      useComposerDraftStore.getState().setModelSelection(threadId, input.modelSelection);
      // Naming the issue is what makes the thread's title and the agent's
      // first move obvious, but the person still decides what to ask for — the
      // prompt is seeded, not sent.
      if (input.planeIssue) {
        useComposerDraftStore
          .getState()
          .setPrompt(
            threadId,
            `Trabaja el issue ${input.planeIssue.ref}: ${input.planeIssue.title}`,
          );
      } else if (input.prompt !== null && input.prompt.length > 0) {
        // An environment operation opens with its message already written and
        // deliberately unsent: the sync, the reset and the copy are the agent's
        // to run, and the person's to authorise by pressing Send.
        useComposerDraftStore.getState().setPrompt(threadId, input.prompt);
      }

      if (input.runSetupScript && worktreePath !== null) {
        queueSetupScript({
          projectId: input.projectId,
          threadId,
          markPendingSetupScript,
        });
      }

      await openThreadAndDispatchCreate({
        threadId,
        navigate,
        openChatThreadPage,
        dispatch: api.orchestration.dispatchCommand,
        command: {
          type: "thread.create",
          commandId: newCommandId(),
          threadId,
          projectId: input.projectId,
          title: trimmedName.length > 0 ? trimmedName : PLACEHOLDER_THREAD_TITLE,
          modelSelection: input.modelSelection,
          runtimeMode: DEFAULT_RUNTIME_MODE,
          interactionMode: planModeByDefault ? "plan" : DEFAULT_PROVIDER_INTERACTION_MODE,
          envMode: input.envMode,
          branch,
          worktreePath,
          // Always stated, never omitted: absent means "server, decide for me",
          // and this thread came from a dialog where the person was asked.
          environmentId: input.environmentId,
          // The mark the server re-checks the agent against, before the thread
          // exists. Always stated so the command reads the same whether it came
          // from a card's button or the ordinary dialog.
          qaOperation: input.qaOperation,
          createdAt,
          planeIssueRef: input.planeIssue?.ref ?? null,
          planeIssueId: input.planeIssue?.id ?? null,
        },
      });

      // After `thread.create`, never folded into it: the decider refuses to link a thread it
      // cannot find, and one fact has one writer — the same command the detail panel uses to
      // change the link later.
      if (input.pullRequest) {
        await api.orchestration.dispatchCommand({
          type: "thread.pull-request.set",
          commandId: newCommandId(),
          threadId,
          pullRequest: {
            projectId: input.pullRequest.projectId,
            number: input.pullRequest.number,
          },
          createdAt,
        });
      }
    },
    [markPendingSetupScript, markTemporaryThread, navigate, openChatThreadPage],
  );
}

/**
 * Leave the project's setup action queued for ChatView to run once the thread
 * is mounted. Running it here is not an option for `terminal` actions: they
 * need the thread's terminal state, which only ChatView owns.
 */
function queueSetupScript(input: {
  projectId: ProjectId;
  threadId: ThreadId;
  markPendingSetupScript: (threadId: ThreadId, scriptId: string) => void;
}): void {
  const project = useStore.getState().projects.find((entry) => entry.id === input.projectId);
  const script = project ? setupProjectScript(project.scripts) : null;
  if (script) {
    input.markPendingSetupScript(input.threadId, script.id);
  }
}
